import { datadogRum, RumFetchResourceEventDomainContext } from '@datadog/browser-rum'
import { UserContext } from 'lib/context/app'
import { GetRUMRequest } from 'pbts/c1/api/accounts/v1/accounts'
import { useContext, useEffect } from 'react'
import { getRUMApi } from 'redux_toolkit/auth'

function redactUrl(url: string): string {
    const urlObj = new URL(url)
    if (!urlObj.pathname.startsWith('/login')) return url

    try {
        if (urlObj.searchParams.has('code')) urlObj.searchParams.set('code', '[REDACTED]')
        if (urlObj.searchParams.has('state')) urlObj.searchParams.set('state', '[REDACTED]')
        return urlObj.toString()
    } catch (e) {
        console.error('Error parsing URL for redaction')
        return '[ERROR REDACTING]'
    }
}

type DatadogSite = 'datadoghq.com' | 'us3.datadoghq.com' | 'us5.datadoghq.com' | 'datadoghq.eu' | 'ddog-gov.com' | 'ap1.datadoghq.com';

function Datadog(): React.ReactElement {
    const { data: rum } = getRUMApi.useGetRUM(GetRUMRequest.create({}))
    const userContext = useContext(UserContext)
    userContext.waitForReady()
    const user = userContext.user

    useEffect(() => {
        if (rum && rum.datadogSite && rum.datadogRumAppId) {
            datadogRum.init({
                applicationId: rum.datadogRumAppId,
                clientToken: rum.datadogRumClientToken,
                site: rum.datadogSite as DatadogSite,
                service: 'conductorone-frontend',
                env: rum.datadogRumEnv,
                version: rum.datadogRumVersion,
                sessionSampleRate: 100,
                sessionReplaySampleRate: rum.datadogRumEnv && rum.datadogRumEnv.includes('sandbox-') ? 100 : 0,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: 'mask-user-input',
                beforeSend: (event, context): boolean => {
                    if (event.type === 'resource') event.resource.url = redactUrl(event.resource.url)
                    if (event.type === 'view') event.view.url = redactUrl(event.view.url)

                    if (event.type === 'resource' && event.resource.type === 'fetch') {
                        const fetchContext = context as RumFetchResourceEventDomainContext

                        if (event.context && fetchContext.response?.headers) {
                            event.context.responseHeaders = Object.fromEntries(fetchContext.response.headers)
                        }
                    }

                    return true // Allow the event to be sent
                },
            })

            datadogRum.setUser({
                id: user?.id,
            })

            datadogRum.startSessionReplayRecording()
        }
    }, [rum, user])

    return <></>
}

export const useErrorReporting = (isError: boolean, error?: Error, component?: string): void => {
    useEffect(() => {
        isError && datadogRum.addError(error, {
            component,
        })
    }, [isError, error, component])
}

export default Datadog
